import { songManager } from '../lib/songManager.js'
import { gameLogic } from '../lib/gameLogic.js'
import { auth, db } from '../lib/supabase.js'

class ScribbleEffects {
  static addRandomScribble(element, type = 'underline') {
    const scribbleColors = ['#E85D75', '#FFB6C1', '#FF69B4']
    const randomColor = scribbleColors[Math.floor(Math.random() * scribbleColors.length)]
    
    if (type === 'underline') {
      element.classList.add('scribble-underline')
    } else if (type === 'circle') {
      element.classList.add('scribble-circle')
    }
  }
  
  static addBackgroundScribbles(container, count = 3) {
    for (let i = 0; i < count; i++) {
      const scribble = document.createElement('div')
      scribble.className = 'scribble-accent'
      scribble.style.position = 'absolute'
      scribble.style.width = `${50 + Math.random() * 100}px`
      scribble.style.height = `${50 + Math.random() * 100}px`
      scribble.style.top = `${Math.random() * 100}%`
      scribble.style.left = `${Math.random() * 100}%`
      scribble.style.opacity = '0.1'
      scribble.style.transform = `rotate(${Math.random() * 360}deg)`
      scribble.style.backgroundImage = `url('/images/scribbles/abstract-${Math.floor(Math.random() * 5) + 1}.svg')`
      scribble.style.backgroundSize = 'contain'
      scribble.style.backgroundRepeat = 'no-repeat'
      scribble.style.pointerEvents = 'none'
      
      container.appendChild(scribble)
    }
  }
}

export class GameComponent {
  constructor(container, user = null) {
    this.container = container
    this.user = user
    
    // Game state
    this.gameSession = null
    this.selectedGenre = 'daily'
    this.gameDate = new Date()
    this.isHardMode = false
    this.isDarkMode = document.body.classList.contains('dark-mode') // Check current state
    this.focusedBlankIndex = null
    this.stats = {
      gamesPlayed: 0,
      gamesWon: 0,
      currentStreak: 0,
      maxStreak: 0
    }
    
    this.initialize()
  }

async initialize() {
  try {
    await this.ensureUserProfile()
    await this.loadUserStats()
    await this.startNewGame()
    this.render()
    this.attachEventListeners()
  } catch (error) {
    console.error('Failed to initialize game:', error)
    this.showError('Failed to load game. Please refresh and try again.')
  }
}

async loadUserStats() {
  if (this.user) {
    try {
      this.stats = await db.getUserStats(this.user.id)
    } catch (error) {
      console.warn('Failed to load user stats:', error)
    }
  } else {
    // Load guest stats from localStorage
    const { localStorageManager } = await import('../lib/localStorage.js')
    this.stats = localStorageManager.getGuestStats()
  }
}

async ensureUserProfile() {
  if (!this.user) return
  
  try {
    const profile = await db.getUserProfile(this.user.id)
    console.log('User profile found:', profile)
    return profile
  } catch (error) {
    console.log('No profile found, creating for:', this.user.email)
    try {
      const newProfile = await db.createUserProfile(this.user.id, this.user.email, 'daily')
      console.log('User profile created successfully:', newProfile)
      return newProfile
    } catch (createError) {
      if (createError.code === '23505' && createError.message.includes('duplicate key')) {
        console.log('User profile already exists (race condition), continuing...')
        try {
          return await db.getUserProfile(this.user.id)
        } catch (fetchError) {
          console.warn('Could not fetch existing profile:', fetchError)
        }
      } else {
        console.error('Failed to create user profile:', createError)
      }
    }
  }
}

async startNewGame() {
  try {
    console.log('Starting new game:', {
      genre: this.selectedGenre,
      date: this.gameDate.toISOString(),
      difficulty: this.isHardMode ? 'hard' : 'easy'
    })
    
    const song = await songManager.getDailySong(
      this.selectedGenre, 
      this.gameDate, 
      this.isHardMode ? 'hard' : 'easy'
    )
    
    console.log('Selected song:', song.title, song.artist)
      
    if (!songManager.validateSong(song)) {
      throw new Error('Invalid song data')
    }
    
    this.gameSession = gameLogic.createGameSession(
      song, 
      this.selectedGenre, 
      this.isHardMode ? 'hard' : 'easy', 
      this.gameDate
    )
    
    // Check for existing game result
    let existingResult = null
    
    if (this.user) {
      // Existing user check
      existingResult = await gameLogic.loadGameResult(
        song, this.selectedGenre, this.isHardMode ? 'hard' : 'easy', this.gameDate, this.user
      )
    } else {
      // Guest user check - CHECK LOCALSTORAGE
      const { localStorageManager } = await import('../lib/localStorage.js')
      const guestHistory = localStorageManager.getGuestGameHistory()
      
      // Look for a matching game in history
      const dateString = this.gameDate.toISOString().split('T')[0]
      existingResult = guestHistory.find(game => 
        game.song_id === song.id &&
        game.genre === this.selectedGenre &&
        game.difficulty === (this.isHardMode ? 'hard' : 'easy') &&
        game.game_date === dateString &&
        game.completed
      )
      
      console.log('Guest game check:', { 
        songId: song.id, 
        genre: this.selectedGenre,
        difficulty: this.isHardMode ? 'hard' : 'easy',
        date: dateString,
        found: !!existingResult 
      })
    }
    
    // If game was already completed, restore that state
    if (existingResult && existingResult.completed) {
      this.gameSession.gameOver = true
      this.gameSession.won = existingResult.won
      this.gameSession.attempts = existingResult.attempts
      this.gameSession.hintsUsed = existingResult.hints_used || 0
      // Fill in the correct answers for display
      this.gameSession.userGuesses = [...this.gameSession.song.blanks]
      console.log('Restored completed game state')
    }
    
  } catch (error) {
    console.error('Failed to start new game:', error)
    throw error
  }
}

render() {
  if (!this.gameSession) {
    this.container.innerHTML = '<div class="loading-content">Loading game...</div>'
    return
  }

  this.container.innerHTML = `
    <style>
      .last-attempt {
        color: var(--error-color, #dc3545);
        font-weight: bold;
      }
    </style>
  <div class="game-background-scribble"></div>
    <div class="header">
      <div class="controls-wrapper">
        <!-- Hamburger menu button (mobile only) -->
        <button class="hamburger-menu" id="hamburgerBtn">
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>
        
        <div class="title-section">
          <img 
            src="/images/Songola_Light_Logo.png" 
            alt="Songola - Think you know the lyrics? Guess again." 
            class="logo light-logo"
          />
          <img 
            src="/images/Songola_Dark_Logo.png" 
            alt="Songola - Think you know the lyrics? Guess again." 
            class="logo dark-logo"
          />
        </div>
      </div>
      
      <!-- Menu overlay for mobile -->
      <div class="menu-overlay" id="menuOverlay"></div>
      
      <div class="controls" id="mobileMenu">
        <button class="btn ${this.isHardMode ? 'active' : 'easy-active'}" id="difficultyBtn">
            ${this.isHardMode ? 'Hard Mode' : 'Easy Mode'}
        </button>
        <button class="btn" id="genreBtn">Choose Genre</button>
        <button class="btn" id="archiveBtn">Previous Games</button>
        <button class="btn" id="darkModeBtn">
            ${this.isDarkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button class="btn" id="statsBtn">Stats</button>
        ${this.user ? 
            `<button class="btn" id="dashboardBtn">Dashboard</button>
            <button class="btn" id="signOutBtn">Sign Out</button>` : 
            `<button class="btn" id="signInBtn">Sign In</button>`
        }
      </div>
    </div>

    <div class="main">
      <div class="game-info">
        <div class="song-info">
          <div class="clickable-info" id="genreInfo">
            <strong>Genre:</strong> <span id="currentGenre">${this.getGenreDisplayName()}</span>
          </div>
          <div class="clickable-info" id="difficultyInfo">
            <strong>Difficulty:</strong> <span id="currentDifficulty">${this.isHardMode ? 'Hard' : 'Easy'}</span>
          </div>
          <div class="clickable-info song-info-date" id="dateInfo">
            <strong>Date:</strong> <span id="gameDateDisplay">${this.gameDate.toDateString()}</span>
          </div>
        </div>
      </div>

      ${!this.gameSession.gameOver ? `
      <div class="lyrics-container" id="lyricsContainer">
        ${this.renderLyrics()}
      </div>

      <div class="guess-input">
        <button class="submit-btn" id="submitBtn">
          Submit All Guesses
        </button>
        <button class="hint-btn ${this.gameSession.hintsUsed >= gameLogic.maxHints ? 'disabled' : ''}" 
                id="hintBtn" 
                ${this.gameSession.hintsUsed >= gameLogic.maxHints ? 'disabled' : ''}>
          Get Hint (<span id="hintsLeft">${gameLogic.maxHints - this.gameSession.hintsUsed}</span> left)
        </button>
      </div>

      <div class="hint-display" id="hintDisplay" style="display: none;"></div>

      <div class="attempts">
        <span id="attemptsText" class="${this.gameSession.attempts === 3 ? 'last-attempt' : ''}">
          ${this.gameSession.attempts === 3 ? 'Last attempt!' : `${4 - this.gameSession.attempts} attempts left`}
        </span>
      </div>
      ` : ''}

    <div class="stats">
      <div class="stat-item">
        <div class="stat-number">${this.stats.gamesPlayed}</div>
        <div class="stat-label">Played</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">${Math.round((this.stats.gamesWon / Math.max(this.stats.gamesPlayed, 1)) * 100)}%</div>
        <div class="stat-label">Win %</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">${this.stats.currentStreak}</div>
        <div class="stat-label">Streak</div>
      </div>
      <div class="stat-item">
        <div class="stat-number">${this.stats.maxStreak}</div>
        <div class="stat-label">Best</div>
      </div>
    </div>

    ${this.gameSession.gameOver ? this.renderGameOverModal() : ''}
  `

  this.addLyricsEventListeners()
}

  // Emoji Animation for Winning
startEmojiAnimation() {
  const emojiContainer = this.container.querySelector('.emoji-container')
  if (!emojiContainer) return
  
  const emoji = emojiContainer.dataset.emoji
  if (!emoji) return

  // this.addCelebrationScribbles() //
  
  // Detect if mobile
  const isMobile = window.innerWidth <= 768
  
  // Use fewer emojis on mobile for better performance
  const emojiCount = isMobile ? 4 : 6
  
  // Create emojis with staggered timing
  for (let i = 0; i < emojiCount; i++) {
    setTimeout(() => {
      const emojiElement = document.createElement('div')
      emojiElement.className = 'rising-emoji'
      emojiElement.setAttribute('data-emoji', emoji) // Use data attribute for ::before content
      emojiElement.style.left = `${15 + (i * (70 / emojiCount))}%` // Distribute evenly
      
      // Set aria-hidden for accessibility
      emojiElement.setAttribute('aria-hidden', 'true')
      
      emojiContainer.appendChild(emojiElement)
      
      // Remove after animation completes
      setTimeout(() => emojiElement.remove(), 5000)
    }, i * 400) // Stagger by 400ms
  }
}

addCelebrationScribbles() {
  const celebrationDiv = document.createElement('div')
  celebrationDiv.className = 'celebration-scribbles'
  
  const scribbleTypes = ['star', 'heart', 'music-note', 'crown', 'sparkle']
  const colors = ['#E85D75', '#FFB6C1', '#FF69B4', '#FF1493', '#FFC0CB']
  
  // Create 10 random scribbles
  for (let i = 0; i < 10; i++) {
    const scribble = document.createElement('div')
    scribble.className = 'celebration-scribble'
    scribble.style.left = `${Math.random() * 100}%`
    scribble.style.animationDelay = `${Math.random() * 2}s`
    
    // Create inline SVG or use background image
    scribble.innerHTML = `
      <svg width="60" height="60" viewBox="0 0 60 60">
        <path d="M30,10 L35,25 L50,25 L38,35 L43,50 L30,40 L17,50 L22,35 L10,25 L25,25 Z" 
              fill="${colors[Math.floor(Math.random() * colors.length)]}" 
              stroke="#000" 
              stroke-width="2"
              transform="rotate(${Math.random() * 360} 30 30)"/>
      </svg>
    `
    
    celebrationDiv.appendChild(scribble)
  }
  
  document.body.appendChild(celebrationDiv)
  
  // Remove after animation completes
  setTimeout(() => {
    celebrationDiv.remove()
  }, 3000)
}

// Add this new method to handle hamburger menu
setupHamburgerMenu() {
  const hamburgerBtn = this.container.querySelector('#hamburgerBtn')
  const mobileMenu = this.container.querySelector('#mobileMenu')
  const menuOverlay = this.container.querySelector('#menuOverlay')
  
  if (!hamburgerBtn || !mobileMenu || !menuOverlay) return
  
  const toggleMenu = () => {
    const isOpen = mobileMenu.classList.contains('open')
    
    if (isOpen) {
      // Close menu
      mobileMenu.classList.remove('open')
      menuOverlay.classList.remove('open')
      hamburgerBtn.classList.remove('open')
      document.body.style.overflow = 'auto'
    } else {
      // Open menu
      mobileMenu.classList.add('open')
      menuOverlay.classList.add('open')
      hamburgerBtn.classList.add('open')
      document.body.style.overflow = 'hidden'
    }
  }
  
  // Toggle menu when hamburger button is clicked
  hamburgerBtn.addEventListener('click', toggleMenu)
  
  // Close menu when overlay is clicked
  menuOverlay.addEventListener('click', toggleMenu)
  
// Close menu when any menu item is clicked
mobileMenu.querySelectorAll('.btn').forEach(btn => {
  btn.addEventListener('click', () => {
    // Close menu immediately
    mobileMenu.classList.remove('open')
    menuOverlay.classList.remove('open')
    hamburgerBtn.classList.remove('open')
    document.body.style.overflow = 'auto'
  })
})
  
  // Close menu on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && mobileMenu.classList.contains('open')) {
      toggleMenu()
    }
  })
}

renderLyrics() {
  if (!this.gameSession || !this.gameSession.song) return ''
  
  let lyricsHtml = this.gameSession.song.lyrics
  const showLetterCount = !this.isHardMode
  
  // IMPORTANT: Trim the lyrics to remove any leading/trailing whitespace or newlines
  lyricsHtml = lyricsHtml.trim()
  
  // Remove any trailing line breaks before processing
  lyricsHtml = lyricsHtml.replace(/\n+$/, '')
  
  // First, convert line breaks to HTML
  lyricsHtml = lyricsHtml.replace(/\n/g, '<br>')
  
  let blankIndex = 0
  lyricsHtml = lyricsHtml.replace(/_____/g, () => {
    const currentIndex = blankIndex
    
    if (currentIndex >= this.gameSession.song.blanks.length) {
      console.error(`Blank index ${currentIndex} exceeds available blanks`)
      return '_____'
    }
    
    const currentGuess = this.gameSession.userGuesses[currentIndex] || ''
    const wordLength = this.gameSession.song.blanks[currentIndex].length
    
    let blankElement
    if (showLetterCount) {
      // Easy mode: Individual letter boxes
      let letterBoxes = ''
      for (let i = 0; i < wordLength; i++) {
        const letter = currentGuess[i] || ''
        letterBoxes += `<input type="text" class="letter-box" data-word-index="${currentIndex}" data-letter-index="${i}" value="${letter}" maxlength="1">`
      }
      blankElement = `<span class="letter-boxes" data-index="${currentIndex}">${letterBoxes}</span>`
    } else {
      // Hard mode: Single input field
      const width = `${Math.max(wordLength * 1.2 + 1, 3)}ch`
      blankElement = `<input type="text" class="blank" data-index="${currentIndex}" id="blank-${currentIndex}" value="${currentGuess}" maxlength="${wordLength + 2}" style="width: ${width};">`
    }
    
    blankIndex++
    return blankElement
  })

  // Remove any trailing <br> tags that might have been added
  lyricsHtml = lyricsHtml.replace(/<br>+$/, '')

  return lyricsHtml
}

renderGameOverModal() {
  const won = this.gameSession.won
  const score = gameLogic.calculateScore(this.gameSession)
  const attempts = this.gameSession.attempts
  
  // Simple title based on attempts
  let celebrationTitle = 'Congratulations!'
  let risingEmoji = ''
  
  if (won) {
    if (attempts === 1) {
      celebrationTitle = 'Rock Star!'
      risingEmoji = '🔥'
    } else if (attempts === 2) {
      celebrationTitle = 'Nice Work!'
      risingEmoji = '🎵'
    } else if (attempts === 3) {
      celebrationTitle = 'You Got It!'
      risingEmoji = '🤘'
    }
  }
  
  // Show correct answers
  let answerHtml = this.gameSession.song.lyrics
  let blankIndex = 0
  answerHtml = answerHtml.replace(/_____/g, () => {
    if (blankIndex < this.gameSession.song.blanks.length) {
      return `<strong style="color: var(--accent-color);">${this.gameSession.song.blanks[blankIndex++]}</strong>`;
    }
    return '_____'
  })
  
  // Convert line breaks to HTML
  answerHtml = answerHtml.replace(/\n/g, '<br>')
  
  // Generate music service links
  const songTitle = encodeURIComponent(this.gameSession.song.title)
  const artistName = encodeURIComponent(this.gameSession.song.artist)
  const searchQuery = encodeURIComponent(`${this.gameSession.song.title} ${this.gameSession.song.artist}`)
  
  const musicLinks = {
    spotify: `https://open.spotify.com/search/${searchQuery}`,
    youtube: `https://www.youtube.com/results?search_query=${searchQuery}`,
    appleMusic: `https://music.apple.com/search?term=${searchQuery}`,
    youtubeMusic: `https://music.youtube.com/search?q=${searchQuery}`
  }

  return `
    <div class="modal" style="display: block;" id="gameOverModal">
      <div class="modal-content game-over-modal">
        <span class="close" id="closeGameOver">&times;</span>
        
        ${won ? `<div class="emoji-container" data-emoji="${risingEmoji}"></div>` : ''}
        
        <h2>${won ? celebrationTitle : 'Game Over'}</h2>
        <p>${won ? 
          `You guessed all the words in ${this.gameSession.attempts} attempts!` : 
          'Better luck next time!'
        }</p>
        ${score ? `<p><strong>Score:</strong> ${score.totalScore} points</p>` : ''}
        
        <div class="song-reveal">
          <h3>The Song Was:</h3>
          <div class="song-info">
            <div><strong>Song:</strong> ${this.gameSession.song.title}</div>
            <div><strong>Artist:</strong> ${this.gameSession.song.artist}</div>
            <div><strong>Genre:</strong> ${this.getGenreDisplayName()}</div>
          </div>
        </div>
        
        <p><strong>The complete lyrics were:</strong></p>
        <div style="background: var(--bg-secondary); padding: 16px; border-radius: 8px; margin: 16px 0; line-height: 1.6; color: var(--text-primary);">
          ${answerHtml}
        </div>
        
        <div style="margin: 20px 0; padding: 16px; background: var(--bg-tertiary); border-radius: 8px; color: var(--text-primary);">
          <p style="margin-bottom: 12px; color: var(--text-primary);"><strong>Daily ${this.getGenreDisplayName()} Challenge Complete!</strong></p>
          <p style="margin: 0; color: var(--text-primary); font-size: 14px;">
            Try a different genre or date, or come back tomorrow for a new song!
          </p>
        </div>
        
        <!-- Music service links moved here -->
        <div class="music-links">
          <p style="margin: 0 0 12px 0; font-size: 14px; color: var(--text-secondary);">Listen to this song:</p>
          <div class="music-service-buttons">
            <a href="${musicLinks.spotify}" target="_blank" rel="noopener noreferrer" class="music-link spotify">
              <span style="font-size: 16px;">🎵</span> Spotify
            </a>
            <a href="${musicLinks.youtube}" target="_blank" rel="noopener noreferrer" class="music-link youtube">
              <span style="font-size: 16px;">▶️</span> YouTube
            </a>
            <a href="${musicLinks.appleMusic}" target="_blank" rel="noopener noreferrer" class="music-link apple-music">
              <span style="font-size: 16px;">🎵</span> Apple Music
            </a>
            <a href="${musicLinks.youtubeMusic}" target="_blank" rel="noopener noreferrer" class="music-link youtube-music">
              <span style="font-size: 16px;">🎶</span> YT Music
            </a>
          </div>
        </div>
        
        <div style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin-top: 20px;">
          <button class="share-btn" id="shareBtn">Share Results</button>
          <button class="share-btn" id="chooseGenreBtn">Choose Different Genre</button>
        </div>
      </div>
    </div>
  `
}

addLyricsEventListeners() {
  const container = this.container.querySelector('#lyricsContainer')
  if (!container) return

  const showLetterCount = !this.isHardMode
  
  if (showLetterCount) {
    // Easy mode: Handle individual letter boxes
    container.querySelectorAll('.letter-box').forEach(letterBox => {
      // Add focus tracking
      letterBox.addEventListener('focus', (e) => {
        this.focusedBlankIndex = parseInt(e.target.dataset.wordIndex)
        e.target.select()
      })
      
      letterBox.addEventListener('input', (e) => {
        const wordIndex = parseInt(e.target.dataset.wordIndex)
        const letterIndex = parseInt(e.target.dataset.letterIndex)
        
        // Update the user's guess for this word
        if (!this.gameSession.userGuesses[wordIndex]) {
          this.gameSession.userGuesses[wordIndex] = ''
        }
        
        let wordGuess = this.gameSession.userGuesses[wordIndex].split('')
        while (wordGuess.length <= letterIndex) wordGuess.push('')
        wordGuess[letterIndex] = e.target.value.toUpperCase()
        this.gameSession.userGuesses[wordIndex] = wordGuess.join('')
        
        // Auto-advance to next letter box
        if (e.target.value && letterIndex < this.gameSession.song.blanks[wordIndex].length - 1) {
          const nextBox = container.querySelector(
            `.letter-box[data-word-index="${wordIndex}"][data-letter-index="${letterIndex + 1}"]`
          )
          if (nextBox) nextBox.focus()
        }
      })
      
      letterBox.addEventListener('keydown', (e) => {
        const wordIndex = parseInt(e.target.dataset.wordIndex)
        const letterIndex = parseInt(e.target.dataset.letterIndex)
        
        if (e.key === 'Backspace' && !e.target.value && letterIndex > 0) {
          // Move to previous box on backspace
          const prevBox = container.querySelector(
            `.letter-box[data-word-index="${wordIndex}"][data-letter-index="${letterIndex - 1}"]`
          )
          if (prevBox) {
            prevBox.focus()
            prevBox.select()
          }
        } else if (e.key === 'Enter') {
          this.submitGuesses()
        }
      })
    })
  } else {
    // Hard mode: Handle regular inputs
    container.querySelectorAll('.blank').forEach(blank => {
      // Add focus tracking
      blank.addEventListener('focus', (e) => {
        this.focusedBlankIndex = parseInt(e.target.dataset.index)
        e.target.select()
      })
      
      blank.addEventListener('click', (e) => {
        e.target.focus()
        e.target.select()
      })
      
      blank.addEventListener('input', (e) => {
        const index = parseInt(e.target.dataset.index)
        this.gameSession.userGuesses[index] = e.target.value
      })
      
      blank.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.submitGuesses()
        }
      })
    })
  }
}

attachEventListeners() {

  // Setup hamburger menu first
  this.setupHamburgerMenu()
    
  // Game controls
  const submitBtn = this.container.querySelector('#submitBtn')
  const hintBtn = this.container.querySelector('#hintBtn')
  const shareBtn = this.container.querySelector('#shareBtn')
  const difficultyBtn = this.container.querySelector('#difficultyBtn')
  const darkModeBtn = this.container.querySelector('#darkModeBtn')
  const signOutBtn = this.container.querySelector('#signOutBtn')
  const signInBtn = this.container.querySelector('#signInBtn')
  const genreBtn = this.container.querySelector('#genreBtn')
  const archiveBtn = this.container.querySelector('#archiveBtn')
  const statsBtn = this.container.querySelector('#statsBtn')
  const dashboardBtn = this.container.querySelector('#dashboardBtn')

  // Make info items clickable
  const genreInfo = this.container.querySelector('#genreInfo')
  const difficultyInfo = this.container.querySelector('#difficultyInfo')
  const dateInfo = this.container.querySelector('#dateInfo')

  if (genreInfo) genreInfo.addEventListener('click', () => this.onGenreModalRequest())
  if (difficultyInfo) difficultyInfo.addEventListener('click', () => this.toggleDifficulty())
  if (dateInfo) dateInfo.addEventListener('click', () => this.onArchiveModalRequest())
  
  // Game over modal controls
  const closeGameOver = this.container.querySelector('#closeGameOver')
  const chooseGenreBtn = this.container.querySelector('#chooseGenreBtn')
  const gameOverModal = this.container.querySelector('#gameOverModal')
  
  if (submitBtn) submitBtn.addEventListener('click', () => this.submitGuesses())
  if (hintBtn) hintBtn.addEventListener('click', () => this.getHint())
  if (shareBtn) shareBtn.addEventListener('click', () => this.shareResults())
  if (difficultyBtn) difficultyBtn.addEventListener('click', () => this.toggleDifficulty())
  if (darkModeBtn) darkModeBtn.addEventListener('click', () => this.toggleDarkMode())
  if (signOutBtn) signOutBtn.addEventListener('click', () => this.signOut())
  if (signInBtn) signInBtn.addEventListener('click', () => this.onSignInRequest())

  if (genreBtn) genreBtn.addEventListener('click', () => this.onGenreModalRequest())
  if (archiveBtn) archiveBtn.addEventListener('click', () => this.onArchiveModalRequest())
  if (statsBtn) statsBtn.addEventListener('click', () => this.onStatsModalRequest())
  if (dashboardBtn) dashboardBtn.addEventListener('click', () => this.onDashboardRequest())
  
  // Game over modal event listeners
  if (closeGameOver) closeGameOver.addEventListener('click', () => this.closeGameOverModal())
  if (chooseGenreBtn) chooseGenreBtn.addEventListener('click', () => {
    this.closeGameOverModal()
    this.onGenreModalRequest()
  })

  // Emoji animation for winning game
  if (gameOverModal && this.gameSession.gameOver && this.gameSession.won) {
    // Small delay to ensure modal is fully rendered
    setTimeout(() => this.startEmojiAnimation(), 100)
  }
  
  // Close modal when clicking outside
  if (gameOverModal) {
    gameOverModal.addEventListener('click', (e) => {
      if (e.target === gameOverModal) {
        this.closeGameOverModal()
      }
    })
  }
  
  // Close modal with ESC key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && gameOverModal && gameOverModal.style.display === 'block') {
      this.closeGameOverModal()
    }
  })
}

  async submitGuesses() {
    if (this.gameSession.gameOver) return
    
    const result = gameLogic.processSubmission(this.gameSession)
    
    if (result.error) {
      alert(result.error)
      return
    }
    
    // Store evaluation results for rendering persistence
    this.gameSession.lastEvaluation = result.evaluation.results
    this.gameSession = result.gameSession
    
    // Apply visual feedback if game is not over
    if (!this.gameSession.gameOver) {
      this.applyGuessResults(result.evaluation.results)
      
      // Wait a moment to show the feedback
      await new Promise(resolve => setTimeout(resolve, 4000))
    }
    
    // Save game result if user is authenticated and game is over
    if (this.gameSession.gameOver) {
      await this.saveGameResult()
      await this.loadUserStats() // Refresh stats
    }
    
    // Re-render to update attempts and game over state
    this.render()
    this.attachEventListeners()
  }

  // EXISTING METHOD - Update this one
  applyGuessResults(results) {
    const showLetterCount = !this.isHardMode
    
    results.forEach((result, index) => {
      const { evaluation } = result
      
      if (showLetterCount) {
        // Easy mode: Style individual letter boxes
        const letterBoxes = this.container.querySelectorAll(`.letter-box[data-word-index="${index}"]`)
        letterBoxes.forEach(box => {
          box.classList.remove('correct', 'partial', 'incorrect')
          if (result.userGuess.trim()) {
            box.classList.add(evaluation)
          }
        })
      } else {
        // Hard mode: Style single input
        const blankElement = this.container.querySelector(`#blank-${index}`)
        if (blankElement) {
          blankElement.classList.remove('correct', 'partial', 'incorrect')
          if (result.userGuess.trim()) {
            blankElement.classList.add(evaluation)
          }
        }
      }
    })
    
    // Show feedback summary
    this.showFeedbackSummary(results)
  }

  // ADD THIS NEW METHOD - Place it after applyGuessResults
  showFeedbackSummary(results) {
    const correctCount = results.filter(r => r.evaluation === 'correct').length
    const partialCount = results.filter(r => r.evaluation === 'partial').length
    const incorrectCount = results.filter(r => r.evaluation === 'incorrect').length
    const totalGuesses = results.filter(r => r.userGuess.trim()).length
    
    if (totalGuesses === 0) return
    
    let message = `Attempt ${this.gameSession.attempts - 1} Results: `
    if (correctCount > 0) message += `✅ ${correctCount} correct `
    if (partialCount > 0) message += `🟨 ${partialCount} close `
    if (incorrectCount > 0) message += `❌ ${incorrectCount} wrong`
    
    // Show temporary feedback message
    this.showTemporaryMessage(message, 'info')
  }

  // ADD THIS NEW METHOD - Place it after showFeedbackSummary
  showTemporaryMessage(message, type = 'info') {
    // Remove any existing feedback message
    const existingMsg = document.querySelector('.temp-feedback-message')
    if (existingMsg) {
      existingMsg.remove()
    }
    
    const messageEl = document.createElement('div')
    messageEl.className = `temp-feedback-message ${type}`
    messageEl.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'info' ? 'var(--accent-color)' : 'var(--error-color)'};
      color: white;
      padding: 12px 20px;
      border-radius: 8px;
      z-index: 1000;
      font-weight: 500;
      box-shadow: var(--shadow-md);
      animation: slideInFade 0.3s ease;
    `
    
    messageEl.textContent = message
    document.body.appendChild(messageEl)
    
    // Add CSS animation
    if (!document.querySelector('#temp-feedback-styles')) {
      const style = document.createElement('style')
      style.id = 'temp-feedback-styles'
      style.textContent = `
        @keyframes slideInFade {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
      `
      document.head.appendChild(style)
    }
    
    // Remove after 3 seconds
    setTimeout(() => {
      if (messageEl.parentNode) {
        messageEl.style.animation = 'slideInFade 0.3s ease reverse'
        setTimeout(() => messageEl.remove(), 300)
      }
    }, 3000)
  }

async getHint() {
  // Use the focused blank index if available, otherwise use the default logic
  const targetIndex = this.focusedBlankIndex !== null ? this.focusedBlankIndex : null
  
  const result = gameLogic.getHint(this.gameSession, targetIndex)
  
  if (result.error) {
    alert(result.error)
    return
  }
  
  this.gameSession = result.gameSession
  
  // Show hint
  const hintDisplay = this.container.querySelector('#hintDisplay')
  hintDisplay.textContent = `Hint for blank ${result.blankIndex + 1}: ${result.hint}`
  hintDisplay.style.display = 'block'
  
  // Update hints left counter
  const hintsLeft = this.container.querySelector('#hintsLeft')
  hintsLeft.textContent = gameLogic.maxHints - this.gameSession.hintsUsed
  
  // The input should already have focus since that's what triggered getting the hint
  
  // Disable hint button if no more hints
  if (this.gameSession.hintsUsed >= gameLogic.maxHints) {
    const hintBtn = this.container.querySelector('#hintBtn')
    hintBtn.disabled = true
    hintBtn.classList.add('disabled')
  }
}

async saveGameResult() {
  console.log('saveGameResult called:', { 
    hasUser: !!this.user, 
    gameOver: this.gameSession?.gameOver,
    userId: this.user?.id 
  })
  
  if (!this.gameSession.gameOver) {
    console.log('Game not over, skipping save')
    return
  }
  
  if (this.user) {
    // Existing user save logic
    console.log('Attempting to save game result for user...')
    try {
      const result = await gameLogic.saveGameResult(this.gameSession, this.user)
      if (result) {
        console.log('✅ Game result saved successfully:', result)
      }
    } catch (error) {
      console.error('❌ Failed to save game result:', error)
    }
  } else {
    // Guest user - save to localStorage
    console.log('Saving game result for guest user...')
    const { localStorageManager } = await import('../lib/localStorage.js')
    
    // Update stats
    this.stats = localStorageManager.updateGuestStats(this.gameSession.won)
    
    // Save game result to history
    const gameResult = {
      song_id: this.gameSession.song.id,
      songs: this.gameSession.song, // Include song data for display
      game_date: this.gameSession.gameDate,
      genre: this.gameSession.genre,
      difficulty: this.gameSession.difficulty,
      attempts: this.gameSession.attempts,
      completed: this.gameSession.gameOver,
      won: this.gameSession.won,
      hints_used: this.gameSession.hintsUsed,
      score: gameLogic.calculateScore(this.gameSession)?.totalScore || 0
    }
    
    localStorageManager.saveGuestGameResult(gameResult)
    console.log('✅ Guest game result saved to localStorage')
  }
}

shareResults() {
  const shareText = gameLogic.generateShareText(this.gameSession)
  
  if (navigator.share) {
    navigator.share({ text: shareText })
  } else {
    navigator.clipboard.writeText(shareText).then(() => {
      alert('Results copied to clipboard!')
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = shareText
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      alert('Results copied to clipboard!')
    })
  }
}

// ADD THE NEW METHOD HERE:
closeGameOverModal() {
  const modal = this.container.querySelector('#gameOverModal')
  if (modal) {
    modal.style.display = 'none'
  }
  
  // You can choose to reset the game or leave it as-is
  // For now, let's just hide the modal and keep the completed state
  console.log('Game over modal closed')
}

async toggleDifficulty() {
  this.isHardMode = !this.isHardMode
  await this.resetGame()
}

  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode
    document.body.classList.toggle('dark-mode', this.isDarkMode)
    
    // Update button text
    const darkModeBtn = this.container.querySelector('#darkModeBtn')
    if (darkModeBtn) {
      darkModeBtn.textContent = this.isDarkMode ? 'Light Mode' : 'Dark Mode'
    }
  }

  async setGenre(genre) {
    this.selectedGenre = genre
    await this.resetGame()
  }

  async setGameDate(date) {
    this.gameDate = new Date(date)
    await this.resetGame()
  }

  async resetGame() {
    try {
      await this.startNewGame()
      this.render()
      this.attachEventListeners()
    } catch (error) {
      console.error('Failed to reset game:', error)
      this.showError('Failed to load new game. Please refresh and try again.')
    }
  }

  getGenreDisplayName() {
    const genreNames = {
      daily: 'Daily Hit',
      rock: 'Rock',
      pop: 'Pop',
      hiphop: 'Hip-Hop',
      country: 'Country',
      rb: 'R&B/Soul',
      indie: 'Indie',
      classic: 'Classic Hits',
      classichiphop: 'Classic Hip-Hop'  // ADD THIS LINE
    }
    
    return genreNames[this.selectedGenre] || this.selectedGenre.charAt(0).toUpperCase() + this.selectedGenre.slice(1)
  }

  showError(message) {
    const errorDiv = document.createElement('div')
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: var(--error-color);
      color: white;
      padding: 16px;
      border-radius: 8px;
      z-index: 1000;
      max-width: 300px;
    `
    errorDiv.textContent = message
    document.body.appendChild(errorDiv)
    
    setTimeout(() => {
      document.body.removeChild(errorDiv)
    }, 5000)
  }

  async signOut() {
    try {
      await auth.signOut()
      this.user = null
      this.onSignOutRequest()
    } catch (error) {
      console.error('Sign out failed:', error)
    }
  }

  // These methods will be set by the main app
  onSignInRequest() {}
  onSignOutRequest() {}
  onGenreModalRequest() {}
  onArchiveModalRequest() {}
  onStatsModalRequest() {}
  onDashboardRequest() {}
}